/**
 * Imports Excel (articles, tiers, familles, lignes de document) : lecture du fichier et
 * découpage d'un import refusé en deux fichiers — « erreurs » (lignes refusées + motif) et
 * « valides » (lignes prêtes à importer).
 *
 * Les valeurs lues ne sont jamais modifiées : les fichiers « erreurs » et « valides » réunis
 * redonnent exactement les lignes du fichier d'origine.
 *
 * Numérotation : l'API désigne une ligne par `row = index dans le tableau envoyé + 2`
 * (ArticleService / TiersService / FamilleService / LigneDocumentVenteService.importBatch).
 * Les lignes vides étant retirées avant l'envoi, ce numéro diffère de la ligne Excel dès
 * qu'il y a une ligne vide dans le fichier : on conserve donc le vrai n° de ligne Excel
 * de chaque ligne envoyée (`lignesExcel`, même index que `lignes`).
 */
import * as XLSX from 'xlsx';

export interface ErreurImport {
  row: number;
  message: string;
}

/** Lignes envoyées à l'API et n° de ligne Excel de chacune (même index). */
export interface LignesImport {
  lignes: Record<string, any>[];
  lignesExcel: number[];
}

export interface FichierImport extends LignesImport {
  feuille: string;      // nom de la première feuille — repris dans les fichiers générés
  colonnes: string[];   // en-têtes, dans l'ordre du fichier
}

export interface LigneRejetee {
  ligne: Record<string, any>;
  ligneExcel: number;
  motif: string;
}

/** Colonnes ajoutées au fichier « erreurs ». Ignorées par l'import (colonnes inconnues) :
 *  le fichier corrigé se réimporte tel quel. Retirées du fichier « valides » (elles y
 *  porteraient le motif périmé d'un import précédent). */
export const COLONNE_LIGNE_EXCEL = 'ligne_excel';
export const COLONNE_ERREUR = 'erreur';
const COLONNES_TECHNIQUES = [COLONNE_LIGNE_EXCEL, COLONNE_ERREUR];

/** Lit la première feuille : lignes non vides (dates → AAAA-MM-JJ, comme avant) + n° de ligne Excel. */
export const lireFichierImport = (data: ArrayBuffer): FichierImport => {
  const workbook = XLSX.read(new Uint8Array(data), { cellDates: true });
  const feuille = workbook.SheetNames[0];
  const parsed = XLSX.utils.sheet_to_json<Record<string, any>>(workbook.Sheets[feuille], { defval: null });

  const lignes: Record<string, any>[] = [];
  const lignesExcel: number[] = [];
  parsed.forEach((row, i) => {
    if (!Object.values(row).some(v => v !== null && v !== '')) return;
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(row)) out[k] = v instanceof Date ? v.toISOString().slice(0, 10) : v;
    lignes.push(out);
    // __rowNum__ : index (base 0) de la ligne dans la feuille, posé par SheetJS (non énumérable).
    const rowNum = (row as any).__rowNum__;
    lignesExcel.push(typeof rowNum === 'number' ? rowNum + 1 : i + 2);
  });

  // defval: null → chaque ligne porte toutes les colonnes de l'en-tête, dans l'ordre du fichier.
  const colonnes = parsed.length > 0 ? Object.keys(parsed[0]) : [];
  return { feuille, colonnes, lignes, lignesExcel };
};

/**
 * Sépare les lignes d'un import refusé. `null` si une erreur ne désigne pas une ligne
 * (erreur globale : document clôturé, droit, réseau…) — rien à découper dans ce cas.
 */
export const separerLignes = (
  lot: LignesImport,
  errors: ErreurImport[],
): { rejetees: LigneRejetee[]; valides: LignesImport } | null => {
  const motifs = new Map<number, string[]>();
  for (const e of errors) {
    const idx = Number(e.row) - 2;
    if (!Number.isInteger(idx) || idx < 0 || idx >= lot.lignes.length) return null;
    motifs.set(idx, [...(motifs.get(idx) ?? []), e.message]);
  }

  const rejetees: LigneRejetee[] = [];
  const valides: LignesImport = { lignes: [], lignesExcel: [] };
  lot.lignes.forEach((ligne, idx) => {
    const m = motifs.get(idx);
    if (m) {
      rejetees.push({ ligne, ligneExcel: lot.lignesExcel[idx], motif: m.join(' ; ') });
    } else {
      valides.lignes.push(ligne);
      valides.lignesExcel.push(lot.lignesExcel[idx]);
    }
  });
  return { rejetees, valides };
};

/** « commande.xlsx » → « commande » ; un fichier déjà généré garde sa base (« commande_erreurs » → « commande »). */
const baseNom = (nomSource: string): string =>
  nomSource.replace(/\.[^.]+$/, '').replace(/_(erreurs|valides)$/, '') || 'import';

const ecrire = (nomFichier: string, feuille: string, colonnes: string[], lignes: Record<string, any>[]) => {
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(lignes, { header: colonnes }), feuille || 'Import');
  XLSX.writeFile(workbook, nomFichier);
};

/** Fichier « erreurs » : lignes refusées telles quelles + n° de ligne Excel + motif. */
export const telechargerErreurs = (
  nomSource: string,
  fichier: { feuille: string; colonnes: string[] },
  rejetees: LigneRejetee[],
) => {
  const colonnes = [...fichier.colonnes.filter(c => !COLONNES_TECHNIQUES.includes(c)), ...COLONNES_TECHNIQUES];
  const lignes = rejetees.map(r => ({ ...r.ligne, [COLONNE_LIGNE_EXCEL]: r.ligneExcel, [COLONNE_ERREUR]: r.motif }));
  ecrire(`${baseNom(nomSource)}_erreurs.xlsx`, fichier.feuille, colonnes, lignes);
};

/** Fichier « valides » : lignes acceptées, telles quelles, prêtes à importer. */
export const telechargerValides = (
  nomSource: string,
  fichier: { feuille: string; colonnes: string[] },
  valides: LignesImport,
) => {
  const colonnes = fichier.colonnes.filter(c => !COLONNES_TECHNIQUES.includes(c));
  const lignes = valides.lignes.map(l => {
    const copie = { ...l };
    for (const c of COLONNES_TECHNIQUES) delete copie[c];
    return copie;
  });
  ecrire(`${baseNom(nomSource)}_valides.xlsx`, fichier.feuille, colonnes, lignes);
};
